import { useNavigate } from 'react-router-dom';
import type { Event } from '@wineryxplorer/shared';

interface EventCardProps {
  event: Event & {
    venue?: {
      name: string;
      address?: {
        city?: string;
        state?: string;
      };
    };
  };
}

const formatEventDate = (date: Date | string | null | undefined): string => {
  if (!date) return 'Date TBD';
  
  try {
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch (error) {
    return 'Invalid Date';
  }
};

const formatEventTime = (startTime?: { hours: number; minutes: number }): string => {
  if (!startTime) return '';
  
  const hours = startTime.hours;
  const minutes = startTime.minutes;
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  const displayMinutes = minutes.toString().padStart(2, '0');
  
  return `${displayHours}:${displayMinutes} ${period}`;
};

const formatEventType = (eventType: string): string => {
  return eventType.charAt(0).toUpperCase() + eventType.slice(1);
};

export const EventCard = ({ event }: EventCardProps) => {
  const navigate = useNavigate();
  
  const isAtCapacity = event.maxAttendees 
    ? event.currentAttendees >= event.maxAttendees 
    : false;

  const handleClick = () => {
    navigate(`/events/${event.id}`);
  };

  return (
    <div
      onClick={handleClick}
      className="bg-gray-100 border border-gray-200 rounded-lg hover:shadow-md transition-shadow cursor-pointer overflow-hidden"
    >
      <div className="p-6">
        {/* Event Type Badge */}
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
            {formatEventType(event.eventType)}
          </span>
          {event.audience === 'members_only' && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-900 border border-amber-300">
              Members Only
            </span>
          )}
          {event.isAllDay && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
              All Day
            </span>
          )}
          {isAtCapacity && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-50 text-primary-800 border border-primary-200">
              At Capacity
            </span>
          )}
        </div>

        {/* Event Name — editorial serif */}
        <h3 className="font-serif text-xl font-normal leading-tight text-gray-900 mb-2">
          {event.name}
        </h3>

        {/* Date and Time */}
        <div className="flex items-center text-sm text-gray-600 mb-2">
          <svg
            className="w-4 h-4 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <span>
            {formatEventDate(event.startDate)}
            {!event.isAllDay && event.startTime && (
              <span className="ml-2">• {formatEventTime(event.startTime)}</span>
            )}
          </span>
        </div>

        {/* Venue */}
        {event.venue && (
          <div className="flex items-center text-sm text-gray-600 mb-2">
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            <span>
              {event.venue.name}
              {event.venue.address?.city && event.venue.address?.state && (
                <span className="text-gray-500">
                  {' '}• {event.venue.address.city}, {event.venue.address.state}
                </span>
              )}
            </span>
          </div>
        )}

        {/* Description Preview */}
        {event.description && (
          <p className="text-sm text-gray-600 line-clamp-2 mt-3">
            {event.description}
          </p>
        )}

        {/* Capacity Indicator */}
        {event.maxAttendees && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">
                {event.currentAttendees} / {event.maxAttendees} attendees
              </span>
              <div className="w-32 bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${
                    isAtCapacity ? 'bg-secondary-700' : 'bg-primary-700'
                  }`}
                  style={{
                    width: `${Math.min(
                      (event.currentAttendees / event.maxAttendees) * 100,
                      100
                    )}%`,
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
